import { useQuery } from "@apollo/client";
import {
  applyNomenclatureToText,
  getCompanyBasePath,
  getNomenclature,
  isLocationNomenclature,
  type Nomenclature,
} from "@shared/constants/nomenclature";

import { CURRENT_USER_QUERY } from "~/api";

type UseNomenclature = {
  /** `"location"` for CMMS accounts, `"company"` otherwise. */
  nomenclature: Nomenclature;
  isLocationNomenclature: boolean;
  /**
   * Rewrites an already-rendered string. For copy that lives in the message
   * catalogue this is unnecessary — `LanguageProvider` has already done it.
   * Use it for literals built in code, e.g. `intl` fallbacks.
   */
  rename: (text?: string | null) => string;
  /** The module's list route: `/app/asset-hub/locations` or `.../companies`. */
  basePath: string;
  /** The detail route for one record, under the same base path. */
  pathTo: (id?: string | null) => string;
};

/**
 * Company → Location nomenclature for copy that is not read through
 * `react-intl` (hard-coded fallbacks, strings assembled at runtime).
 */
const useNomenclature = (): UseNomenclature => {
  const { data } = useQuery(CURRENT_USER_QUERY, { fetchPolicy: "cache-only" });

  const installedProducts = data?.currentUser?.oem?.installedProducts;
  const isLocation = isLocationNomenclature(installedProducts);

  const basePath = getCompanyBasePath(isLocation);

  return {
    nomenclature: getNomenclature(installedProducts),
    isLocationNomenclature: isLocation,
    rename: (text) =>
      !text || !isLocation ? text ?? "" : applyNomenclatureToText(text),
    basePath,
    pathTo: (id) => (id ? `${basePath}/${id}` : basePath),
  };
};

export default useNomenclature;
